import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button, Card, Spinner, Alert, AlertDescription,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  EmptyState, Badge, Input, Label, Textarea,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@agion/ui'
import { Key, Plus, Trash2, ShieldCheck, ShieldOff, ShieldAlert, Wand2, Copy, Check, Eye } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { formatDate } from '@/core/infra_utils'
import { listSshKeys, createSshKey, deleteSshKey, generateSshKey } from './infra_ssh_key.service'
import type { SshKey, GenerateSshKeyResponse } from './infra_ssh_key.service'

export function SSHKeysPage() {
  const { t } = useTranslation(['pages'])
  const [keys, setKeys] = useState<SshKey[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [showGenerate, setShowGenerate] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<SshKey | null>(null)
  const [viewTarget, setViewTarget] = useState<SshKey | null>(null)

  const loadKeys = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const resp = await listSshKeys()
      setKeys(resp.data ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load SSH keys')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadKeys()
  }, [loadKeys])

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('pages:sshKeys.title', 'SSH Keys')}
        description={t('pages:sshKeys.description', 'Manage SSH keys for infrastructure access')}
        icon={Key}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowGenerate(true)}>
              <Wand2 size={14} className="mr-1" />
              {t('pages:sshKeys.generate.title', 'Generate Key')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={14} className="mr-1" />
              {t('pages:sshKeys.addKey', 'Add Key')}
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : keys.length === 0 ? (
        <Card>
          <EmptyState
            icon={Key}
            title={t('pages:sshKeys.empty.title', 'No SSH keys')}
            description={t('pages:sshKeys.empty.description', 'Add an SSH key to enable secure infrastructure access.')}
            action={
              <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
                <Plus size={14} className="mr-1" />
                {t('pages:sshKeys.addKey', 'Add Key')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Table variant="modern">
          <TableHeader variant="modern">
            <TableRow variant="modern">
              <TableHead variant="modern">{t('pages:sshKeys.columns.name', 'Name')}</TableHead>
              <TableHead variant="modern">{t('pages:sshKeys.columns.type', 'Type')}</TableHead>
              <TableHead variant="modern">{t('pages:sshKeys.columns.fingerprint', 'Fingerprint')}</TableHead>
              <TableHead variant="modern">{t('pages:sshKeys.columns.privateKey', 'Private Key')}</TableHead>
              <TableHead variant="modern">{t('pages:sshKeys.columns.created', 'Created')}</TableHead>
              <TableHead variant="modern">{t('pages:sshKeys.columns.actions', 'Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody variant="modern">
            {keys.map(key => (
              <TableRow key={key.id} variant="modern">
                <TableCell className="font-medium">{key.name}</TableCell>
                <TableCell>
                  <Badge variant="default">{key.keyType?.toUpperCase() ?? 'UNKNOWN'}</Badge>
                </TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{key.fingerprint}</TableCell>
                <TableCell>
                  {key.vaultPath ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs">
                      <ShieldCheck size={12} />
                      {t('pages:sshKeys.hasPrivateKey', 'Stored')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-slate-400 text-xs">
                      <ShieldOff size={12} />
                      {t('pages:sshKeys.noPrivateKey', 'Public only')}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-slate-500">{formatDate(key.createdAt)}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setViewTarget(key)}
                    >
                      <Eye size={14} className="mr-1" />
                      {t('pages:sshKeys.viewPublicKey.button', 'View')}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteTarget(key)}
                    >
                      <Trash2 size={14} className="mr-1" />
                      {t('pages:sshKeys.delete', 'Delete')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {showCreate && (
        <CreateSSHKeyDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            void loadKeys()
          }}
        />
      )}

      {showGenerate && (
        <GenerateSSHKeyDialog
          onClose={() => setShowGenerate(false)}
          onGenerated={() => {
            void loadKeys()
          }}
        />
      )}

      {deleteTarget && (
        <DeleteSSHKeyDialog
          sshKey={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null)
            void loadKeys()
          }}
        />
      )}

      {viewTarget && (
        <ViewPublicKeyDialog
          sshKey={viewTarget}
          onClose={() => setViewTarget(null)}
        />
      )}
    </div>
  )
}

function CreateSSHKeyDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation(['pages'])
  const [name, setName] = useState('')
  const [publicKey, setPublicKey] = useState('')
  const [privateKey, setPrivateKey] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit() {
    if (!name.trim() || !publicKey.trim()) return

    try {
      setIsSubmitting(true)
      setError(null)
      await createSshKey({
        name: name.trim(),
        publicKey: publicKey.trim(),
        ...(privateKey.trim() ? { privateKey: privateKey.trim() } : {}),
      } as Parameters<typeof createSshKey>[0])
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pages:sshKeys.create.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('pages:sshKeys.create.title', 'Add SSH Key')}</DialogTitle>
          <DialogDescription>
            {t('pages:sshKeys.description', 'Manage SSH keys for infrastructure access')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && (
            <Alert variant="error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label>{t('pages:sshKeys.create.name', 'Name')}</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('pages:sshKeys.create.namePlaceholder', 'e.g. production-bastion')}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('pages:sshKeys.create.publicKey', 'Public Key')}</Label>
            <Textarea
              value={publicKey}
              onChange={e => setPublicKey(e.target.value)}
              placeholder={t('pages:sshKeys.create.publicKeyPlaceholder')}
              rows={3}
              className="font-mono text-xs"
            />
          </div>

          <div className="space-y-2">
            <Label>{t('pages:sshKeys.create.privateKey', 'Private Key (optional)')}</Label>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('pages:sshKeys.create.privateKeyHint')}
            </p>
            <Textarea
              value={privateKey}
              onChange={e => setPrivateKey(e.target.value)}
              placeholder={t('pages:sshKeys.create.privateKeyPlaceholder')}
              rows={3}
              className="font-mono text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('pages:common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={!name.trim() || !publicKey.trim() || isSubmitting}
          >
            {isSubmitting
              ? t('pages:sshKeys.create.submitting', 'Adding...')
              : t('pages:sshKeys.create.submit', 'Add Key')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeleteSSHKeyDialog({
  sshKey,
  onClose,
  onDeleted,
}: {
  sshKey: SshKey
  onClose: () => void
  onDeleted: () => void
}) {
  const { t } = useTranslation(['pages'])
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleDelete() {
    try {
      setIsDeleting(true)
      setError(null)
      await deleteSshKey(sshKey.id)
      onDeleted()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pages:sshKeys.deleteConfirm.error'))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('pages:sshKeys.deleteConfirm.title', 'Delete SSH Key')}</DialogTitle>
          <DialogDescription>
            {t('pages:sshKeys.deleteConfirm.message')}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <p className="text-sm text-slate-600 dark:text-slate-300">
          <strong>{sshKey.name}</strong>
          <br />
          <span className="font-mono text-xs text-slate-400">{sshKey.fingerprint}</span>
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            {t('pages:common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={isDeleting}
          >
            {isDeleting ? <Spinner className="h-4 w-4 mr-1" /> : <Trash2 size={14} className="mr-1" />}
            {t('pages:sshKeys.deleteConfirm.confirm', 'Delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ViewPublicKeyDialog({
  sshKey,
  onClose,
}: {
  sshKey: SshKey
  onClose: () => void
}) {
  const { t } = useTranslation(['pages'])
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(sshKey.publicKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('pages:sshKeys.viewPublicKey.title', 'Public Key')}</DialogTitle>
          <DialogDescription>
            {t('pages:sshKeys.viewPublicKey.description', 'Public key for {{name}}', { name: sshKey.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Badge variant="default">{sshKey.keyType?.toUpperCase() ?? 'UNKNOWN'}</Badge>
            <span className="font-mono">{sshKey.fingerprint}</span>
          </div>
          <div className="relative">
            <Textarea
              value={sshKey.publicKey}
              readOnly
              rows={3}
              className="font-mono text-xs pr-10"
            />
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-1 right-1"
              onClick={() => void handleCopy()}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            {t('pages:sshKeys.viewPublicKey.close', 'Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function GenerateSSHKeyDialog({
  onClose,
  onGenerated,
}: {
  onClose: () => void
  onGenerated: () => void
}) {
  const { t } = useTranslation(['pages'])
  const [name, setName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generatedKey, setGeneratedKey] = useState<GenerateSshKeyResponse | null>(null)
  const [copiedPublic, setCopiedPublic] = useState(false)
  const [copiedPrivate, setCopiedPrivate] = useState(false)

  async function handleSubmit() {
    if (!name.trim()) return

    try {
      setIsSubmitting(true)
      setError(null)
      const key = await generateSshKey(name.trim())
      setGeneratedKey(key)
      onGenerated()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pages:sshKeys.create.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCopyPublic = async () => {
    if (!generatedKey) return
    try {
      await navigator.clipboard.writeText(generatedKey.publicKey)
      setCopiedPublic(true)
      setTimeout(() => setCopiedPublic(false), 2000)
    } catch { /* clipboard may fail if page not focused */ }
  }

  const handleCopyPrivate = async () => {
    if (!generatedKey?.privateKey) return
    try {
      await navigator.clipboard.writeText(generatedKey.privateKey)
      setCopiedPrivate(true)
      setTimeout(() => setCopiedPrivate(false), 2000)
    } catch { /* clipboard may fail if page not focused */ }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('pages:sshKeys.generate.title', 'Generate SSH Key')}
          </DialogTitle>
          <DialogDescription>
            {t('pages:sshKeys.generate.description', 'Generate an ed25519 keypair. The private key will be stored securely in vault.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && (
            <Alert variant="error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!generatedKey ? (
            <div className="space-y-2">
              <Label>{t('pages:sshKeys.generate.nameLabel', 'Key Name')}</Label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('pages:sshKeys.generate.namePlaceholder', 'e.g., bastion-access')}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <Alert variant="success">
                <AlertDescription>
                  {t('pages:sshKeys.generate.success', 'SSH key generated successfully')}
                </AlertDescription>
              </Alert>

              <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                <ShieldAlert size={14} className="shrink-0" />
                {t('pages:sshKeys.generate.oneTimeWarning', 'Save your private key now — it will not be shown again.')}
              </div>

              <div className="space-y-2">
                <Label>{t('pages:sshKeys.generate.publicKeyLabel', 'Public Key')}</Label>
                <div className="relative">
                  <Textarea
                    value={generatedKey.publicKey}
                    readOnly
                    rows={2}
                    className="font-mono text-xs pr-10"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-1 right-1"
                    onClick={() => void handleCopyPublic()}
                  >
                    {copiedPublic ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </div>
              </div>

              {generatedKey.privateKey && (
                <div className="space-y-2">
                  <Label>{t('pages:sshKeys.generate.privateKeyLabel', 'Private Key')}</Label>
                  <div className="relative">
                    <Textarea
                      value={generatedKey.privateKey}
                      readOnly
                      rows={4}
                      className="font-mono text-xs pr-10"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-1 right-1"
                      onClick={() => void handleCopyPrivate()}
                    >
                      {copiedPrivate ? <Check size={14} /> : <Copy size={14} />}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {!generatedKey ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                {t('pages:common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleSubmit()}
                disabled={!name.trim() || isSubmitting}
              >
                <Wand2 size={14} className="mr-1" />
                {isSubmitting
                  ? t('pages:sshKeys.generate.submitting', 'Generating...')
                  : t('pages:sshKeys.generate.submit', 'Generate')}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={onClose}>
              {t('pages:sshKeys.generate.done', 'Done')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
